<template>
  <ExpandableCard v-model:expanded="isExpanded" class="ship-order-itinerary">
    <template #default>
      <div class="ship-order-itinerary-header">
        <span class="font-medium text-gray-darkest">
          {{
            t('shipOrders.shipOrderNumParam', {
              param: shipOrder.compositeId,
            })
          }}
        </span>

        <!-- TODO: Put ship order status here
        <ShipmentChip
          v-if="shipment?.status && shipment?.health"
          :status="shipment.status"
          :health="shipment.health"
        />
        -->
      </div>

      <LocationMarkers
        :origin="shipOrder.origin"
        :destination="shipOrder.destination"
        hide-location-icons
      />
    </template>

    <template #expansion>
      <ShipOrderItineraryLoadingPanel v-if="transportSegments.loading" />
      <div v-else-if="!hasShipments" class="not-assigned">
        <p>{{ t('shipOrders.notAssigned') }}</p>
        <router-link
          v-if="
            authorization.hasAuthority('shipment', 'consolidate') &&
            isRouteBuilderEnabled
          "
          :to="{
            name: 'routeBuilder',
            query: {
              orderId: shipOrder.orderId,
            },
          }"
        >
          {{ t('routeBuilder.planIt') }}
        </router-link>
      </div>
      <ShipOrderItineraryPanel
        v-else-if="legacyShipment"
        :shipment="legacyShipment"
      />
      <template v-else>
        <ShipOrderItineraryPanel
          v-for="ts in transportSegments.data"
          :key="ts.id"
          :shipment="ts.shipment"
          :transport-segment="ts"
        />
      </template>
    </template>
  </ExpandableCard>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Shipment, ShipmentTransportSegment, ShipOrder } from '@/model';

import LocationMarkers from '@/components/location/LocationMarkers.vue';
import ShipOrderItineraryLoadingPanel from '@/components/order/itinerary/ShipOrderItineraryLoadingPanel.vue';
import ShipOrderItineraryPanel from '@/components/order/itinerary/ShipOrderItineraryPanel.vue';
import ExpandableCard from '@rygen/rygen-ui/components/ui/ExpandableCard.vue';

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipOrder: {
    type: Object as PropType<ShipOrder>,
    required: true,
  },
  shipments: {
    type: Array as PropType<Shipment[]>,
    default: () => [],
  },
});
const { shipOrder, shipments } = toRefs(props);

const isRouteBuilderEnabled = useFlag('route-builder-feat-flag');

const { t } = useI18n();
const toast = useToast();

const transportSegments: Ref<Loadable<ShipmentTransportSegment[]>> = ref({
  loading: false,
});

const segmentShipments = computed(() => {
  const segments = transportSegments.value.data ?? [];
  return segments.map((ts) => ts.shipment);
});

// Legacy shipments may hang directly off the ship order with no transport
// segment, so may not appear among the shipments from transport segments.
const legacyShipment = computed(() =>
  shipments.value.find(
    (s) =>
      s.id === shipOrder.value.shipmentId &&
      !segmentShipments.value.some((seg) => seg.id === s.id),
  ),
);

const hasShipments = computed(() => {
  return !!segmentShipments.value.length || !!legacyShipment.value;
});

const loadAllTransportSegmentsForShipOrder = async (shipOrderId: number) => {
  try {
    transportSegments.value.loading = true;
    transportSegments.value.data =
      await api.shipOrder.findTransportSegmentsForShipOrder(shipOrderId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(
      `Error loading transport segments for ship order ${shipOrderId}`,
      e,
    );
    transportSegments.value.error = e;
    toast.addError({
      error: e,
      detail: t('errors.loadingParamForParamWithId', {
        param: lowerCase(t('shipments.shipments')),
        param2: lowerCase(t('orders.orders')),
        id: shipOrder.value.orderId,
      }),
    });
  } finally {
    transportSegments.value.loading = false;
  }
};

watch(
  () => shipOrder.value.id,
  (newVal) => {
    if (newVal) {
      transportSegments.value = { loading: false };
      loadAllTransportSegmentsForShipOrder(newVal);
    }
  },
  { immediate: true },
);
</script>

<style>
:root {
  /* General properties for this component and all children */
  --ship-order-itinerary-background-color: var(--p-gray-50);

  /* Specific properties for the ShipOrderItineraryCard */
  --ship-order-itinerary-card-header-padding: var(--sp-3) var(--sp-6)
    var(--sp-3) var(--sp-3);
}

.ship-order-itinerary.expandable-card {
  --p-card-body-padding: 0;

  position: relative;

  &.expanded {
    .expandable-card-body {
      padding: var(--ship-order-itinerary-card-header-padding);
    }

    .expandable-card-expansion {
      border: 0;
      padding: 0;

      .not-assigned {
        display: flex;
        gap: var(--gap-1);
        justify-content: space-between;
        align-items: center;
        background-color: var(--ship-order-itinerary-background-color);
        padding: var(--sp-3);
      }
    }
  }

  .expandable-card-body {
    padding: var(--ship-order-itinerary-card-header-padding);

    .expandable-card-content {
      display: flex;
      gap: var(--gap-3);
      justify-content: space-between;
      align-items: center;

      .ship-order-itinerary-header {
        display: flex;
        align-items: center;
        gap: var(--sp-3);
      }
    }
  }
}
</style>
